import { useCallback, useRef, useState } from 'react';
import { useTracking } from 'react-tracking';
import { cn } from '@simplywallst/ui-core/next';
import { NarrativeInvalidationBoundary } from '@/pages/Narrative/components/NarrativeInvalidationBoundary';
import { NarrativeMiniValuator } from '@/pages/Narrative/components/NarrativeMiniValuator';
import { NarrativeCardIsomorphic } from '@/components/NarrativeCard';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Carousel, type CarouselRef } from './Carousel';
import type { Narrative } from '@/pages/Narrative/components/CompanyNarratives/hooks/useCompanyNarratives';

export const CompanyNarrativesList = ({
  canonicalUrl,
  narratives,
  classNameList,
  hasSlider = true,
}: {
  canonicalUrl: string;
  narratives: Narrative[];
  classNameList?: string;
  hasSlider?: boolean;
}) => {
  const { trackEvent } = useTracking();
  const { publicId: currentUserPublicId, isLoggedIn } = useUserProfile();
  const [isDraggingDisabled, setIsDraggingDisabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const emblaRef = useRef<CarouselRef | null>(null);
  const narrativeCount = narratives.length;
  const hasMoreThanOneNarrative = narrativeCount > 1;
  const hasUserNarrative =
    isLoggedIn &&
    narratives.some(
      (narrative) => narrative?.owner?.publicId === currentUserPublicId
    );
  const slideCount = narrativeCount + (hasUserNarrative ? 0 : 1);

  const handleOnClick = useCallback(
    (narrative: (typeof narratives)[number]) => {
      const { id, company } = narrative;
      return trackEvent({
        action: 'click',
        modifier: 'view',
        narrativeId: id,
        companyId: company?.id,
        uniqueSymbol: company?.tickerSymbol,
      });
    },
    [trackEvent]
  );

  const handleSliderInteraction = useCallback(
    (isInteracting: boolean) => {
      setIsDraggingDisabled(isInteracting);
      const currentIndex = emblaRef.current?.selectedScrollSnap();

      if (isInteracting || typeof currentIndex !== 'number')
        return setSelectedIndex(narrativeCount);

      setSelectedIndex(currentIndex);
    },
    [narrativeCount]
  );

  return (
    <NarrativeInvalidationBoundary
      onSetPrimary={({ invalidatePriority }) =>
        invalidatePriority(['narrative-company-report-list'])
      }
    >
      {hasSlider ? (
        // <EmblaCarousel>
        <Carousel
          align="center"
          containScroll="trimSnaps"
          showGradient={hasMoreThanOneNarrative}
          totalSlides={slideCount}
          draggable={!isDraggingDisabled}
          startIndex={selectedIndex}
          ref={emblaRef}
          className="@container/carousel [&>*>div::after]:w-6 [&>*>div::before]:w-6 @[100ch]/carousel:[&>*>div::before]:opacity-60"
        >
          <ul
            className={cn(
              'grid w-full cursor-grab grid-flow-col gap-4 [.is-dragging_&]:cursor-grabbing',
              classNameList
            )}
            style={{ '--slide-count': slideCount } as React.CSSProperties}
          >
            {narratives.map((narrative) => (
              <li className="w-[50ch]" key={narrative.id}>
                <NarrativeCardIsomorphic
                  showCompanyIcon
                  narrative={narrative}
                  onClick={() => handleOnClick(narrative)}
                />
              </li>
            ))}
            {!hasUserNarrative && (
              <li className="w-[60ch]">
                <NarrativeMiniValuator
                  companyCanonicalUrl={canonicalUrl}
                  className="rounded-x1_5 bg-surface-2"
                  hideCompanySelect
                  onSliderInteraction={handleSliderInteraction}
                  titleAs="h2"
                />
              </li>
            )}
          </ul>
        </Carousel>
      ) : (
        // </EmblaCarousel>
        <ul className={cn('grid w-full gap-4', classNameList)}>
          {narratives.map((narrative) => (
            <li key={narrative.id}>
              <NarrativeCardIsomorphic
                showCompanyIcon
                narrative={narrative}
                onClick={() => handleOnClick(narrative)}
              />
            </li>
          ))}
          {!hasUserNarrative && (
            <li>
              <NarrativeMiniValuator
                companyCanonicalUrl={canonicalUrl}
                className="rounded-x1_5 bg-surface-2"
                hideCompanySelect
                onSliderInteraction={handleSliderInteraction}
                titleAs="h2"
              />
            </li>
          )}
        </ul>
      )}
    </NarrativeInvalidationBoundary>
  );
};
